import { z } from "zod";
import type { CheckType } from "@ghostwatch/shared-types";
import { dnsRecordTypeSchema, safeHttpMethodSchema, tagged, uuidSchema } from "./common";

// ---------------------------------------------------------------------------
// Per-variant field schemas. These are what the "create check" dialog binds to
// with react-hook-form (one flat form per selected CheckType) — the dialog
// wraps the validated fields into the externally-tagged wire shape on submit
// via `buildCheckConfig`, matching `CheckConfig` in models.rs exactly.
// ---------------------------------------------------------------------------
const portSchema = z.coerce.number().int().min(1).max(65535);
const nonNegInt = z.coerce.number().int().min(0);
const pct = z.coerce.number().min(0).max(100);

export const httpStatusFields = z.object({
  url: z.string().trim().url("must be a valid URL"),
  method: safeHttpMethodSchema.default("get"),
  expected_statuses: z.array(z.coerce.number().int().min(100).max(599)).default([]),
  capture_headers: z.array(z.string().trim().min(1)).default([]),
  follow_redirects: z.boolean().default(false),
  capture_body_hash: z.boolean().default(false),
  body_excerpt_bytes: nonNegInt.optional(),
  health_token_ref: z.string().trim().optional().or(z.literal("")),
});

export const httpLatencyFields = z.object({
  url: z.string().trim().url("must be a valid URL"),
  method: safeHttpMethodSchema.default("get"),
  threshold_ms: nonNegInt,
});

export const httpHeaderFields = z.object({
  url: z.string().trim().url("must be a valid URL"),
  header: z.string().trim().min(1, "header name is required"),
  expected_contains: z.string().trim().min(1, "expected value is required"),
});

export const tcpConnectFields = z.object({
  host: z.string().trim().min(1, "host is required"),
  port: portSchema,
  expect_tls: z.boolean().default(false),
});

export const tlsCertificateFields = z.object({
  host: z.string().trim().min(1, "host is required"),
  port: portSchema,
  warn_days: nonNegInt.default(14),
  expected_issuer_contains: z.string().trim().optional().or(z.literal("")),
});

export const dnsRecordFields = z.object({
  name: z.string().trim().min(1, "name is required"),
  record_type: dnsRecordTypeSchema,
  expected_values: z.array(z.string().trim().min(1)).optional(),
});

export const domainExpiryFields = z.object({
  domain: z.string().trim().min(1, "domain is required"),
  warn_days: nonNegInt.default(30),
});

export const agentHeartbeatFields = z.object({
  max_missed_seconds: nonNegInt.default(300),
});

export const hostCpuFields = z.object({
  threshold_pct: pct.default(90),
  over_seconds: nonNegInt.default(60),
});

export const hostMemoryFields = z.object({
  threshold_pct: pct.default(90),
  over_seconds: nonNegInt.default(60),
});

export const hostDiskFields = z.object({
  mount: z.string().trim().min(1, "mount is required"),
  threshold_pct: pct.default(90),
});

export const hostLoadFields = z.object({
  threshold_load_1m: z.coerce.number().min(0).default(4),
});

export const serviceStatusFields = z.object({
  service_name: z.string().trim().min(1, "service name is required"),
});

export const processPresenceFields = z.object({
  process_name: z.string().trim().min(1, "process name is required"),
  min_count: nonNegInt.default(1),
});

export const fileIntegrityFields = z.object({
  path: z.string().trim().min(1, "path is required"),
});

export const configDriftFields = z.object({
  baseline_id: uuidSchema,
});

export const ghostwireHealthFields = z.object({
  url: z.string().trim().url("must be a valid URL"),
});

export const jobFreshnessFields = z.object({
  job_name: z.string().trim().min(1, "job name is required"),
  max_age_seconds: nonNegInt.default(3600),
});

/** One entry per `CheckType`, in the same order as `enums.rs`. */
export const checkConfigFieldSchemas = {
  http_status: httpStatusFields,
  http_latency: httpLatencyFields,
  http_header: httpHeaderFields,
  tcp_connect: tcpConnectFields,
  tls_certificate: tlsCertificateFields,
  dns_record: dnsRecordFields,
  domain_expiry: domainExpiryFields,
  agent_heartbeat: agentHeartbeatFields,
  host_cpu: hostCpuFields,
  host_memory: hostMemoryFields,
  host_disk: hostDiskFields,
  host_load: hostLoadFields,
  service_status: serviceStatusFields,
  process_presence: processPresenceFields,
  file_integrity: fileIntegrityFields,
  config_drift: configDriftFields,
  ghostwire_health: ghostwireHealthFields,
  job_freshness: jobFreshnessFields,
} as const satisfies Record<CheckType, z.ZodTypeAny>;

/** Wraps validated per-variant fields into the wire `CheckConfig` shape, e.g. `{ http_status: {...} }`. */
export function buildCheckConfig<T extends CheckType>(
  checkType: T,
  fields: z.infer<(typeof checkConfigFieldSchemas)[T]>,
) {
  return { [checkType]: fields } as Record<T, typeof fields>;
}

/** Full externally-tagged `CheckConfig` union, for runtime-validating data already on the wire. */
export const checkConfigSchema = z.union([
  tagged("http_status", httpStatusFields),
  tagged("http_latency", httpLatencyFields),
  tagged("http_header", httpHeaderFields),
  tagged("tcp_connect", tcpConnectFields),
  tagged("tls_certificate", tlsCertificateFields),
  tagged("dns_record", dnsRecordFields),
  tagged("domain_expiry", domainExpiryFields),
  tagged("agent_heartbeat", agentHeartbeatFields),
  tagged("host_cpu", hostCpuFields),
  tagged("host_memory", hostMemoryFields),
  tagged("host_disk", hostDiskFields),
  tagged("host_load", hostLoadFields),
  tagged("service_status", serviceStatusFields),
  tagged("process_presence", processPresenceFields),
  tagged("file_integrity", fileIntegrityFields),
  tagged("config_drift", configDriftFields),
  tagged("ghostwire_health", ghostwireHealthFields),
  tagged("job_freshness", jobFreshnessFields),
]);

/** `POST /v1/checks` body. Mirrors `CreateCheck` in routes_core.rs. */
export const createCheckSchema = z.object({
  asset_id: uuidSchema,
  name: z.string().trim().min(1, "name is required").max(200),
  configuration: checkConfigSchema,
  interval_seconds: z.coerce.number().int().min(10).max(86_400).optional(),
  timeout_seconds: z.coerce.number().int().min(1).max(120).optional(),
  failure_threshold: z.coerce.number().int().min(1).max(10).optional(),
  recovery_threshold: z.coerce.number().int().min(1).max(10).optional(),
});
export type CreateCheckInput = z.infer<typeof createCheckSchema>;

/** `PATCH /v1/checks/:id` body. Mirrors `UpdateCheck`. */
export const updateCheckSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  enabled: z.boolean().optional(),
  interval_seconds: z.coerce.number().int().min(10).max(86_400).optional(),
  timeout_seconds: z.coerce.number().int().min(1).max(120).optional(),
  configuration: checkConfigSchema.optional(),
});
export type UpdateCheckInput = z.infer<typeof updateCheckSchema>;
